import { h, useEffect, useRef, useState } from "@lukekaalim/act";
import { ThreeJSRoot, a3 } from '@lukekaalim/act-three';
import * as three from 'three';

import classes from './Background.module.css';
import { assetURLs, DrivableCamera, inputBeat, renderBeat, StarField, StarFieldMaterial, useDisposable, useGraphics3DSetup } from "@kosmod/graphics-3d";

import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import { html, ssr, SVG, svg } from "@lukekaalim/act-web";
import { useAsync, useBeat, useKeyboardInput } from "@kosmod/kit";

import "../../../modules/theatre/components/GridChunkRenderer.ts";

import star_hemisphere_url from '../../../dev/assets/star_hemisphere.glb?url';

export const Background = () => {
  const graphics = useGraphics3DSetup({
    onFrame(now) {
      renderBeat.tick(now)
      console.log('draw')
    },
    onResize(canvas) {
      (graphics.camera.get() as three.PerspectiveCamera).aspect = canvas.clientWidth/canvas.clientHeight;
      (graphics.camera.get() as three.PerspectiveCamera).updateProjectionMatrix();
    },
  });

  const ref = useRef<StarField | null>(null);

  useBeat(renderBeat, (now, delta) => {
    const starField = ref.get();
    if (starField) {
      starField.material.origin.x = now / 16000;
    }
  })

  const isClient = ssr.useSSRContext().mode === 'client';

  const { result: resources } = useAsync(async () => {
    if (!isClient)
      return null;

    const [gtlf, starryDiamondTexture] = await Promise.all([
      new GLTFLoader().loadAsync(star_hemisphere_url),
      new three.TextureLoader().loadAsync(assetURLs.starryDiamond),
    ]);

    return {
      starHemisphereMesh: gtlf.scene.children.find(c => c.name === 'StarHemisphere') as three.Mesh,
      starryDiamondTexture,
    };
  }, [isClient])

  useEffect(() => {
    if (resources) {
      (graphics.canvas.get() as HTMLCanvasElement).classList.add(classes.ready)
    }
  }, [resources])

  return [h('canvas', { ref: graphics.canvas, className: classes.background, tabIndex: 0 }, [
    isClient && h(ThreeJSRoot, {}, [
      h(a3.scene, { ref: graphics.scene, background: new three.Color('#030411') }, [
        //h(DrivableCamera, { ref: graphics.camera, keyboard, graphics, aspect: 16/9 }),
        h(a3.perspectiveCamera, {
          ref: graphics.camera,
          zoom: 0,
          position: new three.Vector3(0, 15, 10),
          rotation: new three.Euler(-0.8, 0, 0)
        }),
        //h(WIPField, { value, position }),
        !!resources && [
          h(StarField, {
            volume: 20,
            count: 1024 * 64, height: 30,
            ref, color: new three.Color('#caebf1'),
            texture: resources.starryDiamondTexture,
          }),
          h(a3.mesh, {
            geometry: resources.starHemisphereMesh.geometry,
            material: resources.starHemisphereMesh.material as three.Material,
            scale: new three.Vector3(80, 80, 80),
            position: new three.Vector3(0, 10, 0),
          }),
        ]
      ]),
    ])
  ]),
    // h('input', { type: 'range', valueAsNumber: value, onInput() {
    //   setValue(this.valueAsNumber)
    // }, style: { zIndex: '1' }, min: -5, max: 5, step: 0.001 }),
    // h('input', { type: 'range', valueAsNumber: value, onInput() {
    //   setValue(this.valueAsNumber)
    // }, style: { zIndex: '1' }, min: 0, max: 10, step: 0.001 }),
  ]
};

const WIPField = ({ value, position }: { value: number, position: three.Vector3 }) => {
  const { result: res } = useAsync(async () => {
    const [gtlf, starryDiamondTexture] = await Promise.all([
      new GLTFLoader().loadAsync(star_hemisphere_url),
      new three.TextureLoader().loadAsync(assetURLs.starryDiamond),
    ]);

    return {
      starHemisphereMesh: gtlf.scene.children.find(c => c.name === 'StarHemisphere') as three.Mesh,
      starryDiamondTexture,
    };
  }, [])
  if (!res)
    return null;

  const pointsRef = useRef<three.Points | null>(null);
  const helperRef = useRef<three.Box3Helper | null>(null);

  const geometry = useDisposable(() => {
    const geo = new three.BufferGeometry();

    const positions: number[] = [];
    const flicker: number[] = []
    for (let i  = 0; i < 256 * 256 * 3; i++) {
      positions.push(Math.random(), Math.random(), Math.random())
      flicker.push(Math.random() * Math.PI);
    }

    geo.setAttribute('position', new three.BufferAttribute(new Float32Array(positions), 3));
    geo.setAttribute('flicker_offset', new three.BufferAttribute(new Float32Array(flicker), 1));

    return geo;
  })
  const material = useDisposable(() => {
    const mat = new StarFieldMaterial();
    mat.uniforms.volume.value = 15;
    mat.uniforms.height.value = 10;
    mat.uniforms.diffuse.value = new three.Color('#e5f3f2')
    mat.map = res.starryDiamondTexture;
    mat.uniforms.falloff.value = 1;
    mat.uniforms.map.value = res.starryDiamondTexture;
    mat.uniforms.wave_height.value = 0.2;
    mat.uniforms.wave_speed.value = 0.1;
    mat.uniforms.flicker_speed.value = 0.5;

    // mat.alphaTest = 0.1;

    // // mat.uniforms.size.value = 20;
    // // mat.uniforms.map.value = res.starryDiamondTexture;
    // mat.map = res.starryDiamondTexture;
    // mat.blendDstAlpha = three.SrcAlphaFactor;
    // //mat.size = 0.1;
    // // mat.vertexColors = true
    // mat.transparent = true;
    //mat.defines['USE_COLOR_ALPHA'] = 1;
    //mat.uniforms.color.value = new three.Color('red');

    return mat;
  })

  useBeat(renderBeat, ((now) => {
    //material.uniforms.spawn_origin.value.copy(position)
    material.uniforms.spawn_origin.value.x = now / 30000;
    material.uniforms.now.value = now
    const points = pointsRef.get();
    const helper = helperRef.get()
    // if (points)
    //   points.position.copy(position)
    // if (helper)
    //   helper.box.setFromCenterAndSize(position, new three.Vector3(2, 2, 2))
  }));

  return [
    h(a3.points, { ref: pointsRef, geometry, material, position: { x: -1.5, y: -1, z: -1.5 } }),
    // h(a3.Box3Helper, {
    //   ref: helperRef,
    //   box: new three.Box3().setFromCenterAndSize(
    //     new three.Vector3(-1.5, 0, -1.5),
    //     new three.Vector3(2, 2, 2)
    //   )
    // })
  ]
};